import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { BorderBottomProps } from 'styled-system';
import { borderBottom, space } from 'styled-system';

import { Flex } from 'common/components/Flex';
import Spinner from 'common/components/Spinner';
import type { CommonTypographyProps } from 'common/components/Typography';
import { HeadlineH6 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import type { ListStyleProps } from 'common/theme/system-utilities';
import { listStyle } from 'common/theme/system-utilities';
import { useShowPromoImage } from './hooks/useNavigationItems';
import { NavigationListItems } from './NavigationListItems';
import type { NavigationListProps } from './types';

export const NAV_TITLE_ID = 'navigation-title';

export const NavigationGroup = styled.ul<ListStyleProps & SpaceProps & BorderBottomProps>`
  ${listStyle};
  ${space};
  ${borderBottom};

  &:not(:last-of-type) {
    border-bottom: none;
  }
`;

export const navigationGroupStyles: ComponentProps<typeof NavigationGroup> = {
  listStyle: 'none',
  m: 'sp_0',
  p: 'sp_0',
  mb: 'sp_16',
  borderBottom: 'none',
};

const titleStyles: CommonTypographyProps = {
  py: 'sp_12',
  pl: 'sp_16',
};

const NavigationList = ({
  navigation,
  selectedItem,
  isLoading,
  hideTitle = false,
  ...props
}: NavigationListProps) => {
  const { t } = useLocalization();
  const withPromoImage = useShowPromoImage({
    selectedItem,
    navLevel: props.navLevel,
    isDefaultTab: props.isDefaultTab,
  });

  return (
    <>
      {!hideTitle && (
        <Flex as="header" alignItems="center" gap="sp_12">
          <HeadlineH6 as="h2" id={NAV_TITLE_ID} {...titleStyles}>
            {navigation.name ? navigation.name : t('h24_categories')}
          </HeadlineH6>
          {isLoading && <Spinner size={32} />}
        </Flex>
      )}
      <NavigationGroup
        data-testid={`navigation-group-${props.navLevel}`}
        {...navigationGroupStyles}
      >
        <NavigationListItems
          {...props}
          withPromoImage={withPromoImage}
          items={navigation.children}
          navigation={navigation}
          groupId={props.navLevel}
          selectedItem={selectedItem}
        />
      </NavigationGroup>
    </>
  );
};

export default NavigationList;
